import { Link } from 'react-router-dom';
import { useState } from 'react';
import Logo from '../../components/feature/Logo';

export default function Pricing() {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly' | 'lifetime'>('monthly');
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const packages = [
    {
      name: 'Learnsnap Essentials',
      description: 'Der einfache Einstieg in die KI-gestützte Lernhilfe',
      price: {
        monthly: 9.99,
        yearly: null,
        lifetime: null
      },
      features: [
        'KI-Erklärungen durch Text & kurze Antworten',
        'Verständnisfragen stellen & direkt Feedback erhalten',
        'Kompakte Zusammenfassungen',
        'Tipps zur Test-/Schularbeitsvorbereitung',
        'Ideal für spontane Lernfragen'
      ],
      color: 'from-teal-500 to-emerald-500',
      popular: false,
      telegramAddon: true
    },
    {
      name: 'Learnsnap Pro',
      description: 'Das komplette KI-Nachhilfe-System für nachhaltige Lernfortschritte',
      price: {
        monthly: 19.99,
        yearly: 199,
        lifetime: 499
      },
      credits: {
        monthly: '100 Credits/Monat',
        yearly: '1.500 Credits/Jahr',
        lifetime: '5.000 Credits + lebenslanger Zugang'
      },
      features: [
        'Lehrbuch-Screenshots hochladen',
        'Automatische Fragegenerierung',
        'Analyse deiner Antworten in Sekunden',
        'Erklärwege Schritt für Schritt',
        'Lernmodus & Trainingsmodus',
        'Test-Simulationen',
        'Fortschrittsanalyse mit Stärken/Schwächen',
        'Reflexion nach echten Tests',
        'Belohnungssystem'
      ],
      color: 'from-orange-500 to-amber-500',
      popular: true,
      telegramAddon: true
    }
  ];

  const creditPacks = [
    { name: 'Mini Pack', credits: 30, price: 4.99 },
    { name: 'Starter Pack', credits: 75, price: 9.99 },
    { name: 'Pro Pack', credits: 200, price: 19.99 },
    { name: 'Power Pack', credits: 500, price: 39.99 },
    { name: 'Elite Pack', credits: 1200, price: 79.99 }
  ];

  const telegramPricing = {
    monthly: 4.99,
    yearly: 49,
    lifetime: 149
  };

  const getPrice = (pkg: typeof packages[0]) => {
    if (billingCycle === 'monthly') return pkg.price.monthly;
    if (billingCycle === 'yearly') return pkg.price.yearly;
    return pkg.price.lifetime;
  };

  const getMonthlyEquivalent = (pkg: typeof packages[0]) => {
    if (billingCycle === 'yearly' && pkg.price.yearly) {
      return (pkg.price.yearly / 12).toFixed(2);
    }
    return null;
  };

  const handleBetaSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    // Validierung
    const name = formData.get('name') as string;
    const email = formData.get('email') as string;
    const motivation = formData.get('motivation') as string;
    const school = formData.get('school') as string;

    if (!name || !email || !motivation || !school) {
      setFormStatus('error');
      setFormMessage('Bitte fülle alle Felder aus.');
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    if (motivation.length > 500) {
      setFormStatus('error');
      setFormMessage('Die Motivation darf maximal 500 Zeichen lang sein.');
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d5nm3e59d5134kphbg00', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        setFormMessage('Vielen Dank für deine Bewerbung! Wir melden uns in Kürze bei dir.');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      setFormStatus('error');
      setFormMessage('Es gab einen Fehler beim Absenden. Bitte versuche es später erneut.');
      setTimeout(() => setFormStatus('idle'), 3000);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Schema.org JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "WebPage",
                "@id": `${import.meta.env.VITE_SITE_URL || 'https://learnsnap.online'}/pricing`,
                "url": `${import.meta.env.VITE_SITE_URL || 'https://learnsnap.online'}/pricing`,
                "name": "LearnSnap Preise – Flexible KI-Nachhilfe Pakete",
                "description": "LearnSnap Essentials ab 9,99 € / Monat, LearnSnap Pro ab 19,99 € / Monat. Jährlich und Lifetime verfügbar. Telegram Add-on optional.",
                "inLanguage": "de",
                "isPartOf": { "@id": `${import.meta.env.VITE_SITE_URL || 'https://learnsnap.online'}/#website` }
              },
              {
                "@type": "ItemList",
                "name": "LearnSnap Pakete",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "item": {
                      "@type": "Product",
                      "name": "LearnSnap Essentials",
                      "description": "Der einfache Einstieg in die KI-gestützte Lernhilfe – KI-Erklärungen, Verständnisfragen, Zusammenfassungen und Testvorbereitung.",
                      "brand": { "@type": "Brand", "name": "LearnSnap" },
                      "offers": {
                        "@type": "Offer",
                        "price": "9.99",
                        "priceCurrency": "EUR",
                        "priceValidUntil": "2027-12-31",
                        "availability": "https://schema.org/InStock",
                        "url": `${import.meta.env.VITE_SITE_URL || 'https://learnsnap.online'}/pricing`
                      }
                    }
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "item": {
                      "@type": "Product",
                      "name": "LearnSnap Pro",
                      "description": "Das komplette KI-Nachhilfe-System: Screenshot-Analyse, adaptive Lernpfade, Testvorbereitung, Fortschrittsanalyse und Belohnungssystem.",
                      "brand": { "@type": "Brand", "name": "LearnSnap" },
                      "offers": {
                        "@type": "Offer",
                        "price": "19.99",
                        "priceCurrency": "EUR",
                        "priceValidUntil": "2027-12-31",
                        "availability": "https://schema.org/InStock",
                        "url": `${import.meta.env.VITE_SITE_URL || 'https://learnsnap.online'}/pricing`
                      }
                    }
                  }
                ]
              }
            ]
          })
        }}
      />
      {/* Navigation */}
      <nav className="fixed w-full z-50 bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/">
              <Logo size="md" showText={true} textColor="text-slate-900" />
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="text-sm font-medium text-slate-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap">
                Startseite
              </Link>
              <Link to="/pricing" className="text-sm font-medium text-teal-600 transition-colors cursor-pointer whitespace-nowrap">
                Preise
              </Link>
              <Link to="/dashboard" className="bg-teal-600 text-white px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-teal-700 transition-all cursor-pointer whitespace-nowrap">
                Schüler Login
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <i className="ri-price-tag-3-line"></i>
            <span>Flexible Pakete für jede Lernphase</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6">
            Learnsnap – Programme & Preise
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-8">
            Von der schnellen KI-Erklärung bis zum vollständigen Lernsystem. Telegram ist optional als Zusatz buchbar.
          </p>

          {/* Billing Cycle Toggle */}
          <div className="inline-flex items-center gap-2 bg-white rounded-full p-2 shadow-lg border border-slate-200">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all whitespace-nowrap ${
                billingCycle === 'monthly'
                  ? 'bg-gradient-to-r from-teal-500 to-emerald-500 text-white'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Monatlich
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all whitespace-nowrap ${
                billingCycle === 'yearly'
                  ? 'bg-gradient-to-r from-teal-500 to-emerald-500 text-white'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Jährlich
              <span className="ml-2 text-xs bg-orange-500 text-white px-2 py-0.5 rounded-full">2 Monate gratis</span>
            </button>
            <button
              onClick={() => setBillingCycle('lifetime')}
              className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all whitespace-nowrap ${
                billingCycle === 'lifetime'
                  ? 'bg-gradient-to-r from-teal-500 to-emerald-500 text-white'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Lifetime
              <span className="ml-2 text-xs bg-purple-500 text-white px-2 py-0.5 rounded-full">Einmalig</span>
            </button>
          </div>
        </div>
      </section>

      {/* Beta Tester Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 rounded-3xl p-8 md:p-12 border-2 border-orange-300 shadow-2xl relative overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-orange-200/30 to-amber-200/30 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-yellow-200/30 to-orange-200/30 rounded-full blur-3xl"></div>
            
            <div className="relative z-10">
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 bg-orange-600 text-white px-4 py-2 rounded-full text-sm font-bold mb-4 animate-pulse">
                  <i className="ri-star-line"></i>
                  <span>Limitierte Beta-Phase</span>
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                  Werde Beta-Tester!
                </h2>
                <p className="text-xl text-slate-700 mb-2">
                  Wir nehmen aktuell eine <strong className="text-orange-600">begrenzte Anzahl kostenloser Beta-Tester</strong> auf
                </p>
                <p className="text-slate-600">
                  Bewirb dich jetzt und erhalte vollen Zugang zu allen Features – komplett kostenlos während der Beta-Phase
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 text-center border border-orange-200">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-500 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className="ri-vip-crown-line text-white text-xl"></i>
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2">Kostenloser Zugang</h3>
                  <p className="text-sm text-slate-600">Nutze alle Pro-Features ohne Kosten</p>
                </div>
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 text-center border border-orange-200">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-500 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className="ri-feedback-line text-white text-xl"></i>
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2">Gestalte mit</h3>
                  <p className="text-sm text-slate-600">Dein Feedback formt die Zukunft von LearnSnap</p>
                </div>
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 text-center border border-orange-200">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-500 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className="ri-gift-line text-white text-xl"></i>
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2">Exklusive Vorteile</h3>
                  <p className="text-sm text-slate-600">Erhalte Sonderkonditionen nach der Beta</p>
                </div>
              </div>

              <form id="beta-tester-form" data-readdy-form onSubmit={handleBetaSubmit} className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-slate-200">
                <h3 className="text-2xl font-bold text-slate-900 mb-6">Jetzt bewerben</h3>
                
                <div className="space-y-4 mb-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-slate-700 mb-2">
                      Dein Name <span className="text-orange-600">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all text-sm"
                      placeholder="Max Mustermann"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-2">
                      E-Mail-Adresse <span className="text-orange-600">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all text-sm"
                      placeholder="max@beispiel.de"
                    />
                  </div>

                  <div>
                    <label htmlFor="school" className="block text-sm font-semibold text-slate-700 mb-2">
                      Schule / Universität <span className="text-orange-600">*</span>
                    </label>
                    <input
                      type="text"
                      id="school"
                      name="school"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all text-sm"
                      placeholder="Gymnasium München"
                    />
                  </div>

                  <div>
                    <label htmlFor="grade" className="block text-sm font-semibold text-slate-700 mb-2">
                      Klassenstufe / Semester
                    </label>
                    <input
                      type="text"
                      id="grade"
                      name="grade"
                      className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all text-sm"
                      placeholder="10. Klasse oder 3. Semester"
                    />
                  </div>

                  <div>
                    <label htmlFor="motivation" className="block text-sm font-semibold text-slate-700 mb-2">
                      Warum möchtest du Beta-Tester werden? <span className="text-orange-600">*</span>
                    </label>
                    <textarea
                      id="motivation"
                      name="motivation"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all text-sm resize-none"
                      placeholder="Erzähl uns kurz, warum du LearnSnap testen möchtest und wie du uns helfen kannst, die Plattform zu verbessern... (max. 500 Zeichen)"
                    ></textarea>
                    <p className="text-xs text-slate-500 mt-1">Maximal 500 Zeichen</p>
                  </div>
                </div>

                {formStatus === 'success' && (
                  <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-green-600 text-xl flex-shrink-0"></i>
                    <p className="text-sm text-green-800">{formMessage}</p>
                  </div>
                )}

                {formStatus === 'error' && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                    <i className="ri-error-warning-line text-red-600 text-xl flex-shrink-0"></i>
                    <p className="text-sm text-red-800">{formMessage}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full bg-gradient-to-r from-orange-500 to-amber-500 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:shadow-2xl hover:scale-105 transition-all inline-flex items-center justify-center gap-3 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {formStatus === 'submitting' ? (
                    <>
                      <i className="ri-loader-4-line text-xl animate-spin"></i>
                      <span>Wird gesendet...</span>
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line text-xl"></i>
                      <span>Jetzt bewerben</span>
                    </>
                  )}
                </button>

                <p className="text-xs text-slate-500 text-center mt-4">
                  Nach deiner Bewerbung schalten wir deinen Zugang manuell frei und senden dir eine Bestätigung per E-Mail.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Main Packages */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {packages.map((pkg, index) => {
              const price = getPrice(pkg);
              const monthlyEquiv = getMonthlyEquivalent(pkg);
              
              if (!price) return null;

              return (
                <div
                  key={index}
                  className={`bg-white rounded-3xl p-8 md:p-10 border-2 transition-all hover:shadow-2xl ${
                    pkg.popular ? 'border-orange-500 shadow-xl relative' : 'border-slate-200'
                  }`}
                >
                  {pkg.popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                        ⭐ Beliebteste Wahl
                      </div>
                    </div>
                  )}

                  <div className="text-center mb-8">
                    <h3 className="text-3xl font-bold text-slate-900 mb-3">{pkg.name}</h3>
                    <p className="text-slate-600 mb-6">{pkg.description}</p>
                    
                    <div className="mb-4">
                      <div className="flex items-baseline justify-center gap-2">
                        <span className="text-5xl font-bold text-slate-900">{price.toFixed(2)} €</span>
                        <span className="text-slate-600">
                          {billingCycle === 'monthly' && '/ Monat'}
                          {billingCycle === 'yearly' && '/ Jahr'}
                          {billingCycle === 'lifetime' && 'einmalig'}
                        </span>
                      </div>
                      {monthlyEquiv && (
                        <p className="text-sm text-teal-600 font-medium mt-2">
                          ({monthlyEquiv} € / Monat)
                        </p>
                      )}
                    </div>

                    {pkg.credits && (
                      <div className="inline-flex items-center gap-2 bg-gradient-to-r from-teal-50 to-emerald-50 border border-teal-200 px-4 py-2 rounded-full text-sm font-semibold text-teal-700">
                        <i className="ri-coin-line"></i>
                        <span>{pkg.credits[billingCycle]}</span>
                      </div>
                    )}
                  </div>

                  <div className="space-y-4 mb-8">
                    {pkg.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-check-line text-teal-600 text-sm"></i>
                        </div>
                        <p className="text-slate-700">{feature}</p>
                      </div>
                    ))}
                  </div>

                  {pkg.telegramAddon && (
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200 mb-6">
                      <div className="flex items-start gap-3">
                        <i className="ri-telegram-line text-green-600 text-xl flex-shrink-0"></i>
                        <div>
                          <p className="font-semibold text-slate-900 text-sm mb-1">Optional: Telegram Add-on</p>
                          <p className="text-sm text-slate-600">
                            +{telegramPricing[billingCycle]} € {billingCycle === 'monthly' ? '/Monat' : billingCycle === 'yearly' ? '/Jahr' : 'einmalig'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <Link
                    to="/auth/register"
                    className={`w-full bg-gradient-to-r ${pkg.color} text-white px-8 py-4 rounded-xl text-lg font-semibold hover:shadow-2xl hover:scale-105 transition-all inline-flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer`}
                  >
                    <span>Jetzt starten</span>
                    <i className="ri-arrow-right-line text-xl"></i>
                  </Link>

                  {billingCycle === 'yearly' && pkg.price.yearly && (
                    <p className="text-center text-sm text-teal-600 font-medium mt-4">
                      ✨ Spare über 2 Monate im Vergleich zur monatlichen Zahlung
                    </p>
                  )}
                  {billingCycle === 'lifetime' && pkg.price.lifetime && (
                    <p className="text-center text-sm text-purple-600 font-medium mt-4">
                      🎯 Einmal zahlen, lebenslang nutzen – perfekt für die gesamte Schulzeit
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Telegram Add-on Details */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <i className="ri-telegram-line"></i>
              <span>Optionales Extra</span>
            </div>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Telegram Lernhilfe Add-on</h2>
            <p className="text-xl text-slate-600">
              Ideal als Ergänzung zur Plattform, besonders für Schüler, die viel am Handy lernen
            </p>
          </div>

          <div className="bg-white rounded-3xl p-8 md:p-10 border-2 border-green-200 shadow-xl">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="text-center p-6 bg-green-50 rounded-xl">
                <div className="text-3xl font-bold text-slate-900 mb-2">4,99 €</div>
                <p className="text-slate-600 font-medium">pro Monat</p>
              </div>
              <div className="text-center p-6 bg-green-50 rounded-xl">
                <div className="text-3xl font-bold text-slate-900 mb-2">49 €</div>
                <p className="text-slate-600 font-medium">pro Jahr</p>
              </div>
              <div className="text-center p-6 bg-green-50 rounded-xl">
                <div className="text-3xl font-bold text-slate-900 mb-2">149 €</div>
                <p className="text-slate-600 font-medium">Lifetime</p>
              </div>
            </div>

            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-check-line text-green-600 text-sm"></i>
                </div>
                <p className="text-slate-700">KI beantwortet Lernfragen direkt in Telegram</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-check-line text-green-600 text-sm"></i>
                </div>
                <p className="text-slate-700">Schnelle Hilfe unterwegs</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-check-line text-green-600 text-sm"></i>
                </div>
                <p className="text-slate-700">Kurze Erklärungen & Mini-Zusammenfassungen</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-check-line text-green-600 text-sm"></i>
                </div>
                <p className="text-slate-700">Tägliche Lernimpulse & Prüfungsserien</p>
              </div>
            </div>

            <a
              href="https://t.me/LearnSnapBot"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:shadow-2xl hover:scale-105 transition-all inline-flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-telegram-line text-2xl"></i>
              <span>Telegram Bot testen</span>
            </a>
          </div>
        </div>
      </section>

      {/* Credit Packs */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-violet-100 text-violet-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <i className="ri-coin-line"></i>
              <span>Pay-As-You-Go</span>
            </div>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Credit-System</h2>
            <p className="text-xl text-slate-600">
              Zusätzliche Credits jederzeit flexibel dazukaufen
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {creditPacks.map((pack, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 border-2 border-slate-200 hover:border-violet-300 hover:shadow-xl transition-all text-center"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-violet-500 to-purple-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <i className="ri-coin-line text-white text-2xl"></i>
                </div>
                <h3 className="font-bold text-slate-900 mb-2">{pack.name}</h3>
                <div className="text-3xl font-bold text-violet-600 mb-2">{pack.credits}</div>
                <p className="text-sm text-slate-600 mb-4">Credits</p>
                <div className="text-2xl font-bold text-slate-900 mb-4">{pack.price.toFixed(2)} €</div>
                <Link
                  to="/auth/register"
                  className="w-full bg-gradient-to-r from-violet-500 to-purple-500 text-white px-4 py-2.5 rounded-lg text-sm font-semibold hover:shadow-lg transition-all inline-block whitespace-nowrap cursor-pointer"
                >
                  Kaufen
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Learnsnap */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-teal-50 via-emerald-50 to-cyan-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Warum Learnsnap?</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 border border-slate-200 text-center hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-camera-line text-white text-3xl"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Screenshot hochladen</h3>
              <p className="text-slate-600">KI erklärt & fragt ab – einfach und schnell</p>
            </div>

            <div className="bg-white rounded-2xl p-8 border border-slate-200 text-center hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-time-line text-white text-3xl"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">24/7 verfügbar</h3>
              <p className="text-slate-600">Objektiv & stressfrei, wann immer du willst</p>
            </div>

            <div className="bg-white rounded-2xl p-8 border border-slate-200 text-center hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-purple-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-brain-line text-white text-3xl"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Moderner Lernmodus</h3>
              <p className="text-slate-600">Aktives Lernen statt passivem Lesen</p>
            </div>

            <div className="bg-white rounded-2xl p-8 border border-slate-200 text-center hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-user-line text-white text-3xl"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Individuelle Unterstützung</h3>
              <p className="text-slate-600">Personalisiert auf dein Niveau & Tempo</p>
            </div>

            <div className="bg-white rounded-2xl p-8 border border-slate-200 text-center hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-rose-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-line-chart-line text-white text-3xl"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Fortschritts-Tracking</h3>
              <p className="text-slate-600">Behalte deine Entwicklung im Blick</p>
            </div>

            <div className="bg-white rounded-2xl p-8 border border-slate-200 text-center hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-money-euro-circle-line text-white text-3xl"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Faire Preise</h3>
              <p className="text-slate-600">Flexibles Credit-System für jeden Bedarf</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-teal-500 to-emerald-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Bereit für bessere Noten?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Starte jetzt mit Learnsnap und erlebe, wie KI dein Lernen revolutioniert
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/auth/register"
              className="px-8 py-4 bg-white text-teal-600 rounded-xl font-semibold hover:shadow-2xl transition-all whitespace-nowrap cursor-pointer inline-block"
            >
              <i className="ri-rocket-line mr-2"></i>
              Jetzt kostenlos starten
            </Link>
            <Link
              to="/"
              className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-xl font-semibold hover:bg-white/20 transition-all border-2 border-white/30 whitespace-nowrap cursor-pointer inline-block"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              Zurück zur Startseite
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div>
              <Logo size="sm" showText={true} textColor="text-white" className="mb-4" />
              <p className="text-sm text-slate-400 leading-relaxed">
                KI-gestützte Nachhilfe für jeden - bezahlbar und rund um die Uhr verfügbar.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-base mb-4">Produkt</h4>
              <ul className="space-y-3">
                <li><Link to="/" className="text-sm text-slate-400 hover:text-white transition-colors cursor-pointer">Startseite</Link></li>
                <li><Link to="/pricing" className="text-sm text-slate-400 hover:text-white transition-colors cursor-pointer">Preise</Link></li>
                <li><Link to="/dashboard" className="text-sm text-slate-400 hover:text-white transition-colors cursor-pointer">Schüler Login</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-base mb-4">Rechtliches</h4>
              <ul className="space-y-3">
                <li><Link to="/datenschutz" className="text-sm text-slate-400 hover:text-white transition-colors cursor-pointer">Datenschutz</Link></li>
                <li><Link to="/agb" className="text-sm text-slate-400 hover:text-white transition-colors cursor-pointer">AGB</Link></li>
                <li><Link to="/impressum" className="text-sm text-slate-400 hover:text-white transition-colors cursor-pointer">Impressum</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-base mb-4">Kontakt</h4>
              <div className="space-y-3 text-sm text-slate-400">
                <p className="font-medium text-white">Future Now UG</p>
                <p>Max-Planck-Str. 4<br />85609 Aschheim</p>
                <p>
                  <a href="mailto:info@future-now.online" className="text-teal-400 hover:text-teal-300 transition-colors">
                    info@future-now.online
                  </a>
                </p>
              </div>
            </div>
          </div>
          
          <div className="border-t border-slate-800 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-slate-400 text-sm">
                © 2024 Learnsnap. Alle Rechte vorbehalten.
              </p>
              <div className="flex items-center gap-4">
                <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-slate-300 text-sm transition-colors cursor-pointer">
                  Powered by Readdy
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
